import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
  fetchNextQuestion as fetchNextQuestionApi,
  fetchProgress as fetchProgressApi,
  fetchTimer as fetchTimerApi,
  startAssessment as startAssessmentApi,
  submitAnswer as submitAnswerApi,
  uploadSnapshot as uploadSnapshotApi,
  uploadSelfIntro as uploadSelfIntroApi,
} from '../api/assessments';

const AssessmentContext = createContext(null);

export const AssessmentProvider = ({ children }) => {
  const [assessmentId, setAssessmentId] = useState(null);
  const [timer, setTimer] = useState(null);
  const [progress, setProgress] = useState(null);
  const [selfIntroSubmitted, setSelfIntroSubmitted] = useState(false);

  const startAssessment = useCallback(async (payload) => {
    const data = await startAssessmentApi(payload);
    setAssessmentId(data?.assessmentId || data?.assessment_id);
    setTimer(data?.timer || null);
    setProgress(null);
    setSelfIntroSubmitted(false);
    return data;
  }, []);

  const loadNextQuestion = useCallback(async () => {
    if (!assessmentId) {
      return null;
    }
    const data = await fetchNextQuestionApi(assessmentId);
    if (data?.timer) {
      setTimer(data.timer);
    } else {
      const freshTimer = await fetchTimerApi(assessmentId);
      if (freshTimer) {
        setTimer(freshTimer);
      }
    }
    if (data?.status && data.status !== 'in_progress') {
      setAssessmentId(null);
      setSelfIntroSubmitted(false);
      setTimer(null);
    }
    return data;
  }, [assessmentId]);

  const submitAnswer = useCallback(
    async (payload) => {
      if (!assessmentId) {
        throw new Error('No active assessment.');
      }
      const data = await submitAnswerApi(assessmentId, payload);
      return data;
    },
    [assessmentId]
  );

  const refreshProgress = useCallback(async () => {
    if (!assessmentId) {
      return null;
    }
    const data = await fetchProgressApi(assessmentId);
    if (data?.timer) {
      setTimer(data.timer);
    } else {
      const freshTimer = await fetchTimerApi(assessmentId);
      if (freshTimer) {
        setTimer(freshTimer);
      }
    }
    if (data?.status && data.status !== 'in_progress') {
      setAssessmentId(null);
      setSelfIntroSubmitted(false);
      setTimer(null);
    }
    setProgress(data);
    return data;
  }, [assessmentId]);

  const uploadSnapshot = useCallback(
    async (snapshot) => {
      if (!assessmentId) {
        throw new Error('No active assessment.');
      }
      return uploadSnapshotApi(assessmentId, snapshot);
    },
    [assessmentId]
  );

  const uploadSelfIntro = useCallback(
    async (file) => {
      if (!assessmentId) {
        throw new Error('No active assessment.');
      }
      const response = await uploadSelfIntroApi(assessmentId, file);
      setSelfIntroSubmitted(true);
      return response;
    },
    [assessmentId]
  );

  const resetAssessment = useCallback(() => {
    setAssessmentId(null);
    setTimer(null);
    setProgress(null);
    setSelfIntroSubmitted(false);
  }, []);

  const contextValue = useMemo(
    () => ({
      assessmentId,
      timer,
      progress,
      selfIntroSubmitted,
      startAssessment,
      loadNextQuestion,
      submitAnswer,
      refreshProgress,
      resetAssessment,
      uploadSnapshot,
      uploadSelfIntro,
      setAssessmentId,
    }),
    [
      assessmentId,
      timer,
      progress,
      selfIntroSubmitted,
      startAssessment,
      loadNextQuestion,
      submitAnswer,
      refreshProgress,
      resetAssessment,
      uploadSnapshot,
      uploadSelfIntro,
      setAssessmentId,
    ]
  );

  return (
    <AssessmentContext.Provider value={contextValue}>
      {children}
    </AssessmentContext.Provider>
  );
};

export const useAssessment = () => {
  const context = useContext(AssessmentContext);
  if (!context) {
    throw new Error('useAssessment must be used within an AssessmentProvider');
  }
  return context;
};
